import { useState } from "react";
import { MdAddCircleOutline } from "react-icons/md";
import { InstructionStep, InstructionStepImage } from "utils/interfaces";
import { AnimatePresence, motion } from "framer-motion";
import {
  defaultBgVariants,
  defaultPanelVariants,
} from "../common/defaultValues";
import BackButtonHeaderPopUp from "../common/BackButtonHeader";
import { CreateEditInstructionsContext } from "components/utils/Contexts";
import StepsList from "./StepsList";
import FormPanel from "./FormPanel";
import FullscreenCode from "components/utils/instructions-draggable-panel/fullscreen-code";

interface CreateEditStepByStepInstructionsProps {
  instructionSteps: InstructionStep[];
  setInstructionSteps: React.Dispatch<React.SetStateAction<InstructionStep[]>>;
  handleClosePopUp: () => void;
}

const CreateEditStepByStepInstructions: React.FC<
  CreateEditStepByStepInstructionsProps
> = ({ instructionSteps, setInstructionSteps, handleClosePopUp }) => {
  // States definition
  const [title, setTitle] = useState("");
  const [paragraph, setParagraph] = useState("");
  const [instructionIndexToEdit, setInstructionIndexToEdit] = useState<
    number | null
  >(null);
  const [creationTimestamp, setCreationTimestamp] = useState("");
  // Images states
  const [stepImageInfo, setStepImageInfo] = useState<InstructionStepImage>({
    content: null,
    filename: "",
    size: 0,
    hash: "",
    defaultScale: 1,
  });
  const [codeSoFarInfo, setCodeSoFarInfo] = useState<InstructionStepImage>({
    content: null,
    filename: "",
    size: 0,
    hash: "",
    defaultScale: 1,
  });

  // Display states
  const [firstOpen, setFirstOpen] = useState(true);
  const [includeCodeSoFar, setIncludeCodeSoFar] = useState(false);
  const [showStepImages, setShowStepImages] = useState(false);
  const [initialCodeTab, setInitialCodeTab] = useState(0);
  // Show left panel form (start with the form if it's empty,
  // otherwise when editing, start closed)
  const [showForm, setShowForm] = useState(instructionSteps.length === 0);

  /**
   * Function executed when clicking the discard button
   */
  const handleDiscardClick = () => {
    // Clear all of the fields
    setTitle("");
    setParagraph("");
    setStepImageInfo({
      content: null,
      filename: "",
      size: 0,
      hash: "",
      defaultScale: 1,
    });
    setCodeSoFarInfo({
      content: null,
      filename: "",
      size: 0,
      hash: "",
      defaultScale: 1,
    });

    // And make sure that code so far is reset
    setIncludeCodeSoFar(false);
    // And there's no index to be edited defined
    setInstructionIndexToEdit(null);
  };

  /**
   * Function executed when clicking the "Add new button"
   */
  const handleAddNew = () => {
    // Make sure we are not editing
    setInstructionIndexToEdit(null);
    // Make sure the form is clean
    handleDiscardClick();
    // And open it
    setShowForm(true);
  };

  return (
    <CreateEditInstructionsContext.Provider
      value={{
        instructionSteps,
        title,
        paragraph,
        instructionIndexToEdit,
        creationTimestamp,
        stepImageInfo,
        codeSoFarInfo,
        includeCodeSoFar,
        setTitle,
        setParagraph,
        setInstructionIndexToEdit,
        setCreationTimestamp,
        setStepImageInfo,
        setCodeSoFarInfo,
        setInstructionSteps,
        setShowForm,
        setIncludeCodeSoFar,
        setShowStepImages,
        setInitialCodeTab,
      }}
    >
      <motion.div
        key="add-contact-blurred-background"
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={defaultBgVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={handleClosePopUp}
      />
      <motion.div
        className="fixed max-h-[80vh] bottom-5 left-0 right-0 mx-auto z-30 w-fit h-full px-6 pt-6 pb-8 bg-white rounded-3xl flex-col gap-4"
        variants={defaultPanelVariants}
        initial={{ opacity: 0, y: "100%" }}
        animate={{ opacity: 1, y: 0 }}
        exit="exit"
        transition={{ duration: 0.3, ease: "easeOut" }}
        onAnimationComplete={() => setFirstOpen(false)}
      >
        <div className="max-w-[90vw] w-fit h-full bg-white flex-col justify-start items-start gap-8 inline-flex overflow-x-auto overflow-y-hidden">
          {/* Header */}
          <div className="w-full h-fit inline-flex justify-between">
            {/* Main title */}
            <h1 className="inline-flex gap-2 items-center justify-start">
              <BackButtonHeaderPopUp onClick={handleClosePopUp} />
              <div className="text-neutral-500 text-base text-center font-semibold font-sans leading-[19px] select-none">
                Instructions
              </div>
            </h1>
            {/* Add new button */}
            {!showForm && (
              <div
                className="group inline-flex gap-2 items-center justify-start cursor-pointer"
                onClick={handleAddNew}
              >
                <MdAddCircleOutline className="w-5 h-5 fill-secondary-600 group-hover:fill-secondary-700" />
                <div className="text-secondary-600 group-hover:text-secondary-700 text-lg text-center font-semibold font-sans leading-[22px] select-none">
                  Add new
                </div>
              </div>
            )}
          </div>
          {/* Body */}
          <div className="h-full w-full min-h-0 inline-flex justify-end">
            {/* Form */}
            <AnimatePresence>
              {showForm && (
                <motion.div
                  initial={{ width: firstOpen ? 595 + 65 : 0, opacity: 0 }}
                  animate={{ width: 595 + 65, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="h-full min-h-0 justify-start items-start inline-flex"
                >
                  <FormPanel />
                  <div className="w-fit h-full pl-8 pr-5 justify-center items-center gap-2 inline-flex">
                    <div className="w-px h-full bg-neutral-200" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {/* Steps list */}
            <StepsList />
          </div>
        </div>
      </motion.div>
      {/* Full screen image */}
      <AnimatePresence>
        {showStepImages && (
          <FullscreenCode
            currentStepImage={stepImageInfo}
            fullCodeImage={codeSoFarInfo}
            setShowCodeFullscreen={setShowStepImages}
            initialCodeTab={initialCodeTab}
            showImageScalingOption={true}
            setCurrentStepImage={setStepImageInfo}
            setFullCodeImage={setCodeSoFarInfo}
          />
        )}
      </AnimatePresence>
    </CreateEditInstructionsContext.Provider>
  );
};

export default CreateEditStepByStepInstructions;
